import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getRaces } from '../api';
import useIsMobile from '../useIsMobile';

const pulseKeyframes = `
  @keyframes navpulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.35; }
  }
  @keyframes navring {
    0%   { box-shadow: 0 0 0 0 rgba(249,176,0,0.55); }
    70%  { box-shadow: 0 0 0 6px rgba(249,176,0,0); }
    100% { box-shadow: 0 0 0 0 rgba(249,176,0,0); }
  }
`;

export default function Navbar() {
  const { pathname } = useLocation();
  const [isLive, setIsLive] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hovered, setHovered] = useState(null);
  const isMobile = useIsMobile(640);

  useEffect(() => {
    getRaces()
      .then(races => setIsLive(races.some(r => {
        if (!r.locked || r.official_top10) return false;
        if (!r.race_date) return false;
        const hoursSinceStart = (Date.now() - new Date(r.race_date)) / 36e5;
        return hoursSinceStart < 12;
      })))
      .catch(() => {});
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (path) =>
    pathname === path || (path !== '/' && pathname.startsWith(path + '/'));

  const linkStyle = (key) => {
    const active = isActive(key);
    const hover  = hovered === key && !active;
    return {
      color: active ? '#f9b000' : hover ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.6)',
      textDecoration: 'none',
      fontSize: isMobile ? 12 : 14,
      fontWeight: 600,
      fontFamily: 'var(--font-display)',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      padding: isMobile ? '3px 7px' : '4px 10px',
      borderRadius: 4,
      background: active
        ? 'rgba(249,176,0,0.12)'
        : hover ? 'rgba(255,255,255,0.05)' : 'transparent',
      border: active
        ? '1px solid rgba(249,176,0,0.25)'
        : '1px solid transparent',
      transition: 'color 0.15s, background 0.15s, border-color 0.15s',
      display: 'inline-flex',
      alignItems: 'center',
      whiteSpace: 'nowrap',
    };
  };

  return (
    <nav style={{
      position: 'sticky',
      top: 0,
      zIndex: 100,
      background: scrolled ? 'rgba(10,10,10,0.97)' : 'rgba(13,13,13,0.92)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderBottom: `1px solid ${scrolled ? 'rgba(249,176,0,0.55)' : 'rgba(249,176,0,0.35)'}`,
      borderLeft: '5px solid #e8002d',
      boxShadow: 'inset 3px 0 10px rgba(232,0,45,0.18)',
      padding: '0 20px',
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      height: 60,
      marginBottom: 32,
      transition: 'background 0.3s, border-color 0.3s',
    }}>
      <style>{pulseKeyframes}</style>

      {/* Brand */}
      <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', flexShrink: 0, marginRight: isMobile ? 4 : 8 }}>
        <img src="/brygg_angkvarn_vit_no_text.png" alt="Bryggeriet Ångkvarn" style={{ height: isMobile ? 28 : 34, width: 'auto', flexShrink: 0 }} />
        {!isMobile && (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', lineHeight: 1 }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 9, fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: '0.16em' }}>
              Bryggeriet Ångkvarn
            </span>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 800, color: '#f0f0f0', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 1 }}>
              F1 Pool
            </span>
          </div>
        )}
      </Link>

      {/* Separator */}
      <div style={{ width: 1, height: 20, background: '#1e1e1e', marginRight: isMobile ? 4 : 8, flexShrink: 0 }} />

      {/* Nav links */}
      {[['/', 'Standings'], ['/races', 'Races'], ['/drivers', 'Drivers']].map(([path, label]) => (
        <Link
          key={path}
          to={path}
          style={linkStyle(path)}
          onMouseEnter={() => setHovered(path)}
          onMouseLeave={() => setHovered(null)}
        >
          {label}
        </Link>
      ))}

      {isLive && (
        <Link
          to="/live"
          style={linkStyle('/live')}
          onMouseEnter={() => setHovered('/live')}
          onMouseLeave={() => setHovered(null)}
        >
          <span style={{
            display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
            background: '#f9b000', marginRight: 6, flexShrink: 0,
            animation: 'navpulse 1.2s ease-in-out infinite, navring 1.8s ease-out infinite',
          }} />
          Live
        </Link>
      )}

      {/* Admin — icon only on mobile, muted text on desktop */}
      <Link
        to="/admin"
        style={{
          marginLeft: 'auto',
          color: hovered === '/admin' ? '#666' : '#333',
          textDecoration: 'none',
          fontSize: isMobile ? 16 : 12,
          fontFamily: 'var(--font-display)',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          transition: 'color 0.15s',
          flexShrink: 0,
          lineHeight: 1,
        }}
        onMouseEnter={() => setHovered('/admin')}
        onMouseLeave={() => setHovered(null)}
        aria-label="Admin"
      >
        {isMobile ? '⚙' : '⚙ Admin'}
      </Link>
    </nav>
  );
}
